import {
  assignUsersToAssignment,
  initializePermissionApi,
  PermissionApi,
} from '@data-delivery/permission-ui-api';
import { useMutation } from '@tanstack/react-query';
import { assignmentsKeys } from '../assignments/assignments-keys';
import { permissionQueryClient } from '../query-client';
import { sftpUsersKeys } from '../sftp-users/server/sftp-users-keys';

const sftpUsersApi = initializePermissionApi(PermissionApi.SFTPUsersApi);

export const useCreateSftpUserMutation = () =>
  useMutation<
    string,
    Error,
    PermissionApi.CreateUserDto & { id?: string },
    { prevVisible?: PermissionApi.UserEntity[] }
  >({
    async mutationFn(dto) {
      const { id: _omit, ...body } = dto;

      const res = await sftpUsersApi.createUser({ createUserDto: body });
      return res.data?.id || dto.loginId || `tmp-${Date.now()}`;
    },
    async onMutate(dto) {
      const visibleQuery = sftpUsersKeys.visible();
      await permissionQueryClient.cancelQueries({ queryKey: visibleQuery.queryKey });
      const prevVisible = permissionQueryClient.getQueryData<PermissionApi.UserEntity[]>(
        visibleQuery.queryKey,
      );
      const id = dto.id || dto.loginId || `tmp-${Date.now()}`;

      const rawLogin = dto.loginId.trim();
      const isSystemUser = Boolean(dto.isSystemUser);
      const providedFirst = dto.firstName?.trim();
      const providedLast = dto.lastName?.trim();
      let firstName: string;
      let lastName: string;
      if (isSystemUser) {
        const localPart = rawLogin.includes('@') ? rawLogin.split('@')[0] : rawLogin;
        const base = localPart.replace(/\s+/g, '');
        const safe = base.length > 0 ? base : 'system';
        firstName = safe;
        lastName = safe;
      } else {
        firstName = providedFirst && providedFirst.length > 0 ? providedFirst : rawLogin;
        lastName = providedLast && providedLast.length > 0 ? providedLast : rawLogin;
      }

      const assignments =
        Array.isArray(dto.assignments) && dto.assignments.length > 0
          ? dto.assignments
          : ['pending-assignment'];

      const optimistic: PermissionApi.UserEntity = {
        id,
        idmId: dto.idmId,
        loginId: dto.loginId,
        firstName,
        lastName,
        isSystemUser,
        isLoginAdmin: Boolean(dto.isLoginAdmin),
        assignments,
        lastModified: Date.now(),
        lastModifiedBy: 'optimistic-user',
        contentAccessRole: dto.contentAccessRole || '',
      };
      permissionQueryClient.setQueryData(visibleQuery.queryKey, [
        ...(prevVisible || []),
        optimistic,
      ]);
      return { prevVisible };
    },
    onError(_err, _vars, ctx) {
      if (ctx?.prevVisible) {
        permissionQueryClient.setQueryData(sftpUsersKeys.visible().queryKey, ctx.prevVisible);
      }
    },
    onSuccess() {
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.all() });
      permissionQueryClient.invalidateQueries({ queryKey: assignmentsKeys.all() });
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.visible().queryKey });
    },
  });

export const useUpdateSftpUserMutation = () =>
  useMutation<
    string,
    Error,
    { id: string; changes: PermissionApi.UpdateUserDto },
    { prevVisible?: PermissionApi.UserEntity[]; prevById?: PermissionApi.UserEntity }
  >({
    async mutationFn({ id, changes }) {
      await sftpUsersApi.updateUser({ userId: id, updateUserDto: changes });
      return id;
    },
    async onMutate({ id, changes }) {
      const visibleQuery = sftpUsersKeys.visible();
      await permissionQueryClient.cancelQueries({ queryKey: visibleQuery.queryKey });
      const prevVisible = permissionQueryClient.getQueryData<PermissionApi.UserEntity[]>(
        visibleQuery.queryKey,
      );
      const prevById = permissionQueryClient.getQueryData<PermissionApi.UserEntity>(
        sftpUsersKeys.byId(id),
      );
      if (prevVisible) {
        const patched = prevVisible.map(u =>
          u.id === id ? { ...u, ...changes, lastModified: Date.now() } : u,
        );
        permissionQueryClient.setQueryData(visibleQuery.queryKey, patched);
      }

      if (prevById) {
        permissionQueryClient.setQueryData(sftpUsersKeys.byId(id), {
          ...prevById,
          ...changes,
          lastModified: Date.now(),
        });
      }

      return { prevVisible, prevById };
    },
    onError(_err, _vars, ctx) {
      if (ctx?.prevVisible) {
        permissionQueryClient.setQueryData(sftpUsersKeys.visible().queryKey, ctx.prevVisible);
      }

      if (ctx?.prevById) {
        permissionQueryClient.setQueryData(sftpUsersKeys.byId(ctx.prevById.id), ctx.prevById);
      }
    },
    onSuccess(_data, vars) {
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.all() });
      permissionQueryClient.invalidateQueries({ queryKey: assignmentsKeys.all() });

      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.byId(vars.id) });
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.visible().queryKey });
    },
  });

export const useAddAssignmentToUserMutation = () =>
  useMutation<
    { userId: string; assignmentId: string },
    Error,
    { userId: string; assignmentId: string },
    { prevAssignments?: string[] }
  >({
    async mutationFn({ userId, assignmentId }) {
      await sftpUsersApi.addAssignment({ userId, addAssignmentDto: { assignmentId } });
      return { userId, assignmentId };
    },
    async onMutate({ userId, assignmentId }) {
      const key = sftpUsersKeys.assignments(userId);
      await permissionQueryClient.cancelQueries({ queryKey: key });
      const prevAssignments = permissionQueryClient.getQueryData<string[]>(key);
      permissionQueryClient.setQueryData(key, [...(prevAssignments || []), assignmentId]);
      return { prevAssignments };
    },
    onError(_err, vars, ctx) {
      if (ctx?.prevAssignments) {
        permissionQueryClient.setQueryData(
          sftpUsersKeys.assignments(vars.userId),
          ctx.prevAssignments,
        );
      }
    },
    onSuccess({ userId }) {
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.assignments(userId) });
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.byId(userId) });
    },
  });

export const useRemoveAssignmentFromUserMutation = () =>
  useMutation<
    { userId: string; assignmentId: string },
    Error,
    { userId: string; assignmentId: string },
    { prevAssignments?: string[] }
  >({
    async mutationFn({ userId, assignmentId }) {
      await sftpUsersApi.removeAssignment({ userId, assignmentId });
      return { userId, assignmentId };
    },
    async onMutate({ userId, assignmentId }) {
      const key = sftpUsersKeys.assignments(userId);
      await permissionQueryClient.cancelQueries({ queryKey: key });
      const prevAssignments = permissionQueryClient.getQueryData<string[]>(key);
      if (prevAssignments) {
        permissionQueryClient.setQueryData(
          key,
          prevAssignments.filter(a => a !== assignmentId),
        );
      }

      return { prevAssignments };
    },
    onError(_err, vars, ctx) {
      if (ctx?.prevAssignments) {
        permissionQueryClient.setQueryData(
          sftpUsersKeys.assignments(vars.userId),
          ctx.prevAssignments,
        );
      }
    },
    onSuccess({ userId }) {
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.assignments(userId) });
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.byId(userId) });
    },
  });

export const useAddSshKeyToUserMutation = () =>
  useMutation<
    { userId: string; keyId: string },
    Error,
    { userId: string; key: PermissionApi.SSHKeyEntity },
    { prevKeys?: PermissionApi.SSHKeyEntity[]; userId?: string }
  >({
    async mutationFn({ userId, key }) {
      await sftpUsersApi.addSSHKey({ userId, sSHKeyEntity: key });
      return { userId, keyId: key.identifier };
    },
    async onMutate({ userId, key }) {
      const keyQuery = sftpUsersKeys.sshKeys(userId);
      await permissionQueryClient.cancelQueries({ queryKey: keyQuery });
      const prevKeys = permissionQueryClient.getQueryData<PermissionApi.SSHKeyEntity[]>(keyQuery);
      permissionQueryClient.setQueryData(keyQuery, [...(prevKeys || []), key]);
      return { prevKeys, userId };
    },
    onError(_err, _vars, ctx) {
      if (ctx?.prevKeys && ctx.userId) {
        permissionQueryClient.setQueryData(sftpUsersKeys.sshKeys(ctx.userId), ctx.prevKeys);
      }
    },
    onSuccess({ userId }) {
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.sshKeys(userId) });
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.byId(userId) });
    },
  });

export const useEditSshKeyForUserMutation = () =>
  useMutation<
    { userId: string; keyId: string },
    Error,
    { userId: string; keyId: string; changes: Partial<PermissionApi.SSHKeyEntity> },
    { prevKeys?: PermissionApi.SSHKeyEntity[]; userId?: string }
  >({
    async mutationFn({ userId, keyId, changes }) {
      const entity: PermissionApi.SSHKeyEntity = {
        identifier: keyId,
        key: changes.key || '',
        comments: changes.comments || '',
        algorithm: changes.algorithm || '',
      };
      await sftpUsersApi.editSSHKey({ userId, keyId, sSHKeyEntity: entity });
      return { userId, keyId };
    },
    async onMutate({ userId, keyId, changes }) {
      const keyQuery = sftpUsersKeys.sshKeys(userId);
      await permissionQueryClient.cancelQueries({ queryKey: keyQuery });
      const prevKeys = permissionQueryClient.getQueryData<PermissionApi.SSHKeyEntity[]>(keyQuery);
      if (prevKeys) {
        const patched = prevKeys.map(k =>
          k.identifier === keyId ? { ...k, ...changes, lastModified: Date.now() } : k,
        );
        permissionQueryClient.setQueryData(keyQuery, patched);
      }

      return { prevKeys, userId };
    },
    onError(_err, _vars, ctx) {
      if (ctx?.prevKeys && ctx.userId) {
        permissionQueryClient.setQueryData(sftpUsersKeys.sshKeys(ctx.userId), ctx.prevKeys);
      }
    },
    onSuccess({ userId }) {
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.sshKeys(userId) });
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.byId(userId) });
    },
  });

export const useDeleteSshKeyForUserMutation = () =>
  useMutation<
    { userId: string; keyId: string },
    Error,
    { userId: string; keyId: string },
    { prevKeys?: PermissionApi.SSHKeyEntity[]; userId?: string }
  >({
    async mutationFn({ userId, keyId }) {
      await sftpUsersApi.deleteSSHKey({ userId, keyId });
      return { userId, keyId };
    },
    async onMutate({ userId, keyId }) {
      const keyQuery = sftpUsersKeys.sshKeys(userId);
      await permissionQueryClient.cancelQueries({ queryKey: keyQuery });
      const prevKeys = permissionQueryClient.getQueryData<PermissionApi.SSHKeyEntity[]>(keyQuery);
      if (prevKeys) {
        permissionQueryClient.setQueryData(
          keyQuery,
          prevKeys.filter(k => k.identifier !== keyId),
        );
      }

      return { prevKeys, userId };
    },
    onError(_err, _vars, ctx) {
      if (ctx?.prevKeys && ctx.userId) {
        permissionQueryClient.setQueryData(sftpUsersKeys.sshKeys(ctx.userId), ctx.prevKeys);
      }
    },
    onSuccess({ userId }) {
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.sshKeys(userId) });
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.byId(userId) });
    },
  });

export const useResetSftpUserPasswordMutation = () =>
  useMutation<void, Error, { userId: string; newPassword: string }>({
    async mutationFn({ userId, newPassword }) {
      await sftpUsersApi.resetUserPassword({
        userId,
        resetUserPasswordDto: { newPassword },
      });
    },
    onSuccess(_data, vars) {
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.byId(vars.userId) });
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.visible().queryKey });
    },
  });

export const useBulkAssignAssignmentsToUserMutation = () =>
  useMutation<
    { userId: string; addAssignments: string[]; removeAssignments: string[] },
    Error,
    { userId: string; addAssignments: string[]; removeAssignments: string[] }
  >({
    async mutationFn({ userId, addAssignments, removeAssignments }) {
      await sftpUsersApi.bulkAssignAssignments({
        userId,
        manageUserAssignmentsDto: { addAssignments, removeAssignments },
      });
      return { userId, addAssignments, removeAssignments };
    },
    onSuccess(_data, vars) {
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.assignments(vars.userId) });
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.byId(vars.userId) });
      permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.visible().queryKey });
    },
  });

export const useAssignUsersToAssignmentMutation = () =>
  useMutation<
    { assignmentId: string; addUsers: string[]; removeUsers: string[] },
    Error,
    { assignmentId: string; addUsers: string[]; removeUsers: string[] },
    { prevVisible?: PermissionApi.UserEntity[] }
  >({
    async mutationFn({ assignmentId, addUsers, removeUsers }) {
      await assignUsersToAssignment(assignmentId, { addUsers, removeUsers });
      return { assignmentId, addUsers, removeUsers };
    },
    async onMutate({ assignmentId, addUsers, removeUsers }) {
      const visibleQuery = sftpUsersKeys.visible();
      await permissionQueryClient.cancelQueries({ queryKey: visibleQuery.queryKey });
      const prevVisible = permissionQueryClient.getQueryData<PermissionApi.UserEntity[]>(
        visibleQuery.queryKey,
      );

      if (prevVisible) {
        const addSet = new Set(addUsers);
        const removeSet = new Set(removeUsers);

        const patched = prevVisible.map(user => {
          const isAdded = addSet.has(user.id);
          const isRemoved = removeSet.has(user.id);

          if (!isAdded && !isRemoved) {
            return user;
          }

          const currentAssignments = Array.isArray(user.assignments) ? user.assignments : [];
          const kept = currentAssignments.filter(a => a !== assignmentId);
          const finalAssignments = isAdded ? [...kept, assignmentId] : kept;

          return {
            ...user,
            assignments: finalAssignments,
            lastModified: Date.now(),
          };
        });

        permissionQueryClient.setQueryData(visibleQuery.queryKey, patched);
      }

      return { prevVisible };
    },
    onError(_err, _vars, ctx) {
      if (ctx?.prevVisible) {
        permissionQueryClient.setQueryData(sftpUsersKeys.visible().queryKey, ctx.prevVisible);
      }
    },
    onSuccess() {
      return Promise.all([
        permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.all() }),
        permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.visible().queryKey }),
        permissionQueryClient.invalidateQueries({ queryKey: assignmentsKeys.all() }),
      ]);
    },
  });

export const useDeleteSftpUserMutation = () =>
  useMutation<
    PermissionApi.DeleteUserResponseDto,
    Error,
    { userId: string },
    { prevVisible?: PermissionApi.UserEntity[] }
  >({
    async mutationFn({ userId }) {
      const res = await sftpUsersApi.deleteUser({ userId });
      return res.data;
    },
    async onMutate({ userId }) {
      const visibleQuery = sftpUsersKeys.visible();
      await permissionQueryClient.cancelQueries({ queryKey: visibleQuery.queryKey });
      const prevVisible = permissionQueryClient.getQueryData<PermissionApi.UserEntity[]>(
        visibleQuery.queryKey,
      );
      if (prevVisible) {
        permissionQueryClient.setQueryData(
          visibleQuery.queryKey,
          prevVisible.filter(u => u.id !== userId),
        );
      }

      return { prevVisible };
    },
    onError(_err, _vars, ctx) {
      if (ctx?.prevVisible) {
        permissionQueryClient.setQueryData(sftpUsersKeys.visible().queryKey, ctx.prevVisible);
      }
    },
    onSuccess() {
      return Promise.all([
        permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.all() }),
        permissionQueryClient.invalidateQueries({ queryKey: sftpUsersKeys.visible().queryKey }),
      ]);
    },
  });
